import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface OfflineEntry {
  direction: string;
  event_id: string;
  user_id: string;
  created_at: string;
}

interface OfflineContextType {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  syncNow: () => Promise<void>;
}

const STORAGE_KEY = "partyflow_offline_entries";
const EVENTS_CACHE_KEY = "partyflow_offline_events";

const OfflineContext = createContext<OfflineContextType | null>(null);

function getPendingEntries(): OfflineEntry[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function savePendingEntries(entries: OfflineEntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

export function addOfflineEntry(entry: OfflineEntry) {
  const entries = getPendingEntries();
  entries.push(entry);
  savePendingEntries(entries);
  window.dispatchEvent(new Event("offline-entry-added"));
}

export function getCachedEvents() {
  try {
    return JSON.parse(localStorage.getItem(EVENTS_CACHE_KEY) || "[]");
  } catch {
    return [];
  }
}

export function cacheEvents(events: any[]) {
  localStorage.setItem(EVENTS_CACHE_KEY, JSON.stringify(events));
}

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(getPendingEntries().length);
  const justReconnectedRef = useRef(false);

  const syncNow = useCallback(async () => {
    const entries = getPendingEntries();
    if (entries.length === 0) return;

    setIsSyncing(true);
    try {
      const { error } = await supabase.from("count_entries").insert(entries);
      if (!error) {
        savePendingEntries([]);
        setPendingCount(0);
      }
    } catch {
      // Will retry later
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // Listen for online/offline
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      justReconnectedRef.current = true;
    };
    const handleOffline = () => {
      setIsOnline(false);
      justReconnectedRef.current = false;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Auto-sync when back online
  useEffect(() => {
    if (isOnline && justReconnectedRef.current) {
      justReconnectedRef.current = false;
      syncNow();
    }
  }, [isOnline, syncNow]);

  // Listen for new offline entries
  useEffect(() => {
    const handler = () => setPendingCount(getPendingEntries().length);
    window.addEventListener("offline-entry-added", handler);
    return () => window.removeEventListener("offline-entry-added", handler);
  }, []);

  return (
    <OfflineContext.Provider value={{ isOnline, isSyncing, pendingCount, syncNow }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error("useOffline must be used within OfflineProvider");
  return ctx;
}
